// Group Booking Types for BarbrIQ Platform

export enum GroupBookingType {
  Birthday = 'Birthday',
  BachelorParty = 'BachelorParty',
  CorporateEvent = 'CorporateEvent',
  WeddingParty = 'WeddingParty',
  Other = 'Other'
}

export enum ParticipantStatus {
  Invited = 'Invited',
  Confirmed = 'Confirmed',
  Declined = 'Declined',
  Attended = 'Attended',
  NoShow = 'NoShow'
}

export enum GroupBookingStatus {
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled'
}

export enum PaymentType {
  OrganizerPaysAll = 'OrganizerPaysAll',
  SplitPayment = 'SplitPayment',
  IndividualPayment = 'IndividualPayment'
}

export interface ParticipantDto {
  id?: number;
  name: string;
  email: string;
  phone?: string;
  status: ParticipantStatus;
  serviceIds: number[];
  assignedTimeSlot?: string;
  notes?: string;
  invitedAt?: string;
  confirmedAt?: string;
  declinedAt?: string;
  attendedAt?: string;
}

export interface GroupBookingServiceDto {
  serviceId: number;
  serviceName?: string;
  serviceCategory?: string;
  durationMinutes: number;
  priceCharged: number;
}

export interface GroupBookingDto {
  id: number;
  eventType: GroupBookingType;
  eventName: string;
  eventDate: string;
  eventTime: string;
  participantCount: number;
  status: GroupBookingStatus;
  paymentType: PaymentType;

  // Organizer info
  organizerId: string;
  organizerName: string;
  organizerEmail: string;
  organizerPhone?: string;

  // Barbershop & Barbers
  barbershopId: number;
  barbershopName: string;
  barbershopAddress: string;
  barberIds: number[];
  barberNames: string[];

  // Services
  services: GroupBookingServiceDto[];
  totalDurationMinutes: number;
  totalPrice: number;
  depositAmount: number;
  depositPaid: boolean;
  depositPaidAt?: string;

  // Participants
  participants: ParticipantDto[];
  confirmedParticipants: number;
  declinedParticipants: number;

  // Special instructions
  specialInstructions?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
  cancellationReason?: string;
}

export interface CreateGroupBookingDto {
  eventType: GroupBookingType;
  eventName: string;
  eventDate: string;
  eventTime: string;
  participantCount: number;
  barbershopId: number;
  barberIds: number[];
  serviceIds: number[];
  paymentType: PaymentType;
  specialInstructions?: string;
  /**
   * Estimated total event duration, derived client-side from the selected
   * services' durations, participant count, barber count, and scheduling
   * type (GAP-10 / FEAT-1 — backend previously always stored 0).
   */
  estimatedDurationMinutes?: number;
  participants: Omit<ParticipantDto, 'id' | 'status' | 'invitedAt' | 'confirmedAt' | 'declinedAt' | 'attendedAt'>[];
}

export interface UpdateGroupBookingDto {
  eventName?: string;
  eventDate?: string;
  eventTime?: string;
  participantCount?: number;
  barberIds?: number[];
  serviceIds?: number[];
  specialInstructions?: string;
  status?: GroupBookingStatus;
}

export interface AddParticipantDto {
  name: string;
  email: string;
  phone?: string;
  serviceIds: number[];
  assignedTimeSlot?: string;
  notes?: string;
}

export interface UpdateParticipantDto {
  name?: string;
  email?: string;
  phone?: string;
  serviceIds?: number[];
  assignedTimeSlot?: string;
  notes?: string;
  status?: ParticipantStatus;
}

export interface InvitationDto {
  participantIds: number[];
  customMessage?: string;
  sendEmail: boolean;
  sendSms: boolean;
}

/** Lifecycle status of a tokenized group invitation (BOOK-2a wire contract). */
export type GroupInvitationStatus =
  | 'Pending'
  | 'Accepted'
  | 'Declined'
  | 'Expired'
  | 'Cancelled';

export interface GroupBookingInvitation {
  id: number;
  groupBookingId: number;
  eventName: string;
  /** yyyy-MM-dd */
  eventDate: string;
  /** HH:mm */
  eventTime: string;
  barbershopName: string;
  organizerName: string;
  participantName: string;
  participantEmail: string;
  customMessage?: string;
  invitationToken: string;
  status: GroupInvitationStatus;
  sentAt: string;
  expiresAt: string;
  openedAt?: string;
  respondedAt?: string;
  response?: 'Accepted' | 'Declined';
}

/**
 * Response of POST /{id}/deposit/create-payment-intent (BOOK-2c wire contract).
 * The clientSecret is confirmed client-side with Stripe Elements, then
 * confirm-deposit is called with the paymentIntentId.
 */
export interface GroupDepositPaymentIntentResponse {
  groupBookingId: number;
  clientSecret: string | null;
  paymentIntentId: string | null;
  /** Outstanding amount the intent charges (deposit required − deposit paid). */
  amountDue: number;
  /** The full required deposit for the event. */
  depositAmount: number;
}

export interface GroupBookingStatistics {
  totalGroupBookings: number;
  upcomingGroupBookings: number;
  completedGroupBookings: number;
  cancelledGroupBookings: number;
  totalParticipants: number;
  averageParticipantsPerEvent: number;
  totalRevenue: number;
  averageRevenuePerEvent: number;
  mostPopularEventType: GroupBookingType;
  confirmationRate: number;
}

// Wizard step data
export interface GroupBookingWizardStep1 {
  eventType: GroupBookingType;
  eventName: string;
  eventDate: string;
  eventTime: string;
  participantCount: number;
  specialInstructions: string;
}

export interface GroupBookingWizardStep2 {
  serviceIds: number[];
  sameServiceForAll: boolean;
  selectedServices: {
    serviceId: number;
    serviceName: string;
    durationMinutes: number;
    price: number;
  }[];
}

export interface GroupBookingWizardStep3 {
  barbershopId: number;
  barbershopName: string;
  barberIds: number[];
  barbers: {
    id: number;
    name: string;
    photo?: string;
  }[];
  schedulingType: 'Simultaneous' | 'Sequential';
}

export interface GroupBookingWizardStep4 {
  participants: AddParticipantDto[];
}

export interface GroupBookingWizardStep5 {
  paymentType: PaymentType;
  depositRequired: boolean;
  depositAmount: number;
  agreeToTerms: boolean;
}

export interface GroupBookingWizardData {
  step1: GroupBookingWizardStep1;
  step2: GroupBookingWizardStep2;
  step3: GroupBookingWizardStep3;
  step4: GroupBookingWizardStep4;
  step5: GroupBookingWizardStep5;
}
